/**
 * Serializes an error or unknown value into a loggable object.
 * Handles Error instances, plain objects, and other types.
 */
export function serializeError(error: unknown): Record<string, unknown> | undefined {
  if (error === null || error === undefined) {
    return undefined;
  }

  if (typeof error === 'object' && error !== null && !(error instanceof Error)) {
    if (error.constructor === Object) {
      return error as Record<string, unknown>;
    }
    return { value: String(error), type: error.constructor.name };
  }

  if (error instanceof Error) {
    const errorObj: Record<string, unknown> = {
      name: error.name || 'Error',
      message: error.message || 'Unknown error',
      ...(error.stack && { stack: error.stack }),
    };

    if ('cause' in error && error.cause) {
      const serializedCause = serializeError(error.cause);
      if (serializedCause) {
        errorObj.cause = serializedCause;
      }
    }

    return { error: errorObj };
  }

  return { error: String(error), type: typeof error };
}

/**
 * Serializes multiple arguments into a structured object for logging.
 * Handles various argument patterns for backward compatibility.
 */
export function serializeArgs(...args: unknown[]): Record<string, unknown> | undefined {
  if (args.length === 0) {
    return undefined;
  }

  if (args.length === 1) {
    const arg = args[0];
    if (typeof arg === 'object' && arg !== null && !(arg instanceof Error) && arg.constructor === Object) {
      return arg as Record<string, unknown>;
    }
    return serializeError(arg);
  }

  const result: Record<string, unknown> = {};

  args.forEach((arg, index) => {
    if (arg === null || arg === undefined) {
      result[`arg${index}`] = arg;
    } else if (typeof arg === 'object' && !(arg instanceof Error) && arg.constructor === Object) {
      const obj = arg as Record<string, unknown>;
      Object.keys(obj).forEach(key => {
        result[`arg${index}_${key}`] = obj[key];
      });
    } else if (arg instanceof Error) {
      const serialized = serializeError(arg);
      if (serialized) {
        Object.assign(result, serialized);
        result[`arg${index}_error`] = true;
      }
    } else {
      result[`arg${index}`] = arg;
    }
  });

  return Object.keys(result).length > 0 ? result : undefined;
}

/**
 * Build log record with message so pretty-print shows a human-readable
 * msg. Serialized is spread first so the caller's message always wins (avoids
 * serialized.error.msg or other keys overwriting the log message).
 */
export function withMessageFirst(message: string, serialized: Record<string, unknown>): Record<string, unknown> {
  return { ...serialized, msg: message };
}
